import { useAuthContext } from '@authentication/Authentication';
import { Button } from '@components/button';
import { Group } from '@components/layout';
import { Paper, Stack, ThemeProvider, Typography } from '@mui/material';
import { customTheme, getThemeMode } from '@theme/Theme';
import { BaseHeader } from './header';

type RegisterAuthCheckProps = {
  /** Path to redirect to after logout (e.g. /register or /register-as). Defaults to /register. */
  registerRedirectPath?: string;
};

/**
 * Renders "Already Logged In" UI when user is authenticated during registration flow.
 * Used on register and register-as pages to let users go home or log out before registering.
 */
export const RegisterAuthCheck = ({
  registerRedirectPath = '/register',
}: RegisterAuthCheckProps) => {
  const { user, logout } = useAuthContext();

  return (
    <ThemeProvider theme={customTheme(getThemeMode())}>
      <Stack sx={{ minHeight: '100vh' }}>
        <BaseHeader />
        <Stack alignItems="center" justifyContent="center" flex={1}>
          <Paper elevation={3} sx={{ maxWidth: 480, width: '100%', p: 6 }}>
            <Stack gap={8} alignItems="center">
              <Typography
                textAlign="center"
                sx={{ fontSize: '1.5rem', fontWeight: 700, lineHeight: '2rem' }}
              >
                Already Logged In
              </Typography>
              <Typography variant="body2" textAlign="center" color="text.paper">
                You are currently logged in as <strong>{user?.email}</strong>. You must log out
                before registering a new account.
              </Typography>
              <Group gap={4}>
                <Button
                  variant="secondary"
                  onClick={() => {
                    if (typeof window !== 'undefined') {
                      window.location.href = '/';
                    }
                  }}
                >
                  Go to Home
                </Button>
                <Button
                  variant="accent"
                  onClick={() => {
                    if (typeof window !== 'undefined') {
                      logout(`${window.location.origin}${registerRedirectPath}`);
                    }
                  }}
                >
                  Log out and Register
                </Button>
              </Group>
            </Stack>
          </Paper>
        </Stack>
      </Stack>
    </ThemeProvider>
  );
};
